/**
 * Per-card display config keyed by backend signal_type.
 *
 * Agent labels (chip + section header), descriptions, and the agent icon
 * all come from the backend AGENT_REGISTRY via the company-details API.
 * This file holds only the per-CARD presentation details that are
 * inherently frontend concerns:
 *
 * - icon: shown inside the card body (still per-signal so the two
 *   Follow-Ups subtypes — schedule_follow_up and overdue_reminder — can
 *   carry their own visual cue inside the card)
 * - linkTarget: where the card title navigates (deal vs company vs auto)
 * - formatLabel: the days-based subtitle ("2d unreplied", "Due today")
 *
 * The chip header label comes from `RecommendationAgent.singular_label`
 * via the company-details API — never hardcoded here.
 */

import type { IconType } from 'react-icons'
import {
  PiAddressBook,
  PiArrowRight,
  PiBell,
  PiCalendarCheck,
  PiCoffee,
  PiEnvelope,
  PiMegaphone,
  PiTrendUp,
} from 'react-icons/pi'
import type {
  SignalType,
  EnrichedAction,
} from '../../services/recommendations_contracts'

export interface SignalTypeConfig {
  /** Phosphor icon (via react-icons/pi) shown inside the card body */
  icon: IconType
  /**
   * Click target for the card's deal/company link.
   * 'deal' always links to the deal, 'company' always to the company,
   * 'auto' links to deal if deal_id exists, else company.
   *
   * Mirrored on the backend in
   * `server/recommendations/usecases/apply_recommendation.py` as
   * `_NOTE_TARGET_BY_SIGNAL` so `add_note` lands on the same entity the
   * card title navigates to. Update both files when adding a signal type.
   */
  linkTarget: 'deal' | 'company' | 'auto'
  /** Format the days-based label shown in the card subtitle */
  formatLabel: (action: EnrichedAction) => string
}

export const SIGNAL_CONFIG: Record<SignalType, SignalTypeConfig> = {
  unreplied_email: {
    icon: PiEnvelope,
    linkTarget: 'company',
    formatLabel: (a) => `${a.days_unreplied ?? 0}d unreplied`,
  },
  inactive_deal: {
    icon: PiCoffee,
    linkTarget: 'deal',
    formatLabel: (a) => {
      if (a.days_in_stage && a.days_in_stage >= 14) {
        return `${a.days_in_stage}d in ${a.current_stage || 'stage'}`
      }
      return `${a.days_stale ?? 0}d inactive`
    },
  },
  email_stage_change: {
    icon: PiArrowRight,
    linkTarget: 'deal',
    formatLabel: (a) =>
      `${a.days_in_stage ?? 0}d in ${a.current_stage || 'stage'}`,
  },
  schedule_follow_up: {
    icon: PiCalendarCheck,
    linkTarget: 'company',
    formatLabel: (a) => `${a.days_since_email ?? 0}d ago`,
  },
  overdue_reminder: {
    icon: PiBell,
    linkTarget: 'auto',
    formatLabel: (a) => {
      const d = a.days_overdue
      if (d == null) return 'Due soon'
      if (d > 0) return `${d}d overdue`
      if (d === 0) return 'Due today'
      return `Due in ${Math.abs(d)}d`
    },
  },
  contact_suggestion: {
    icon: PiAddressBook,
    linkTarget: 'company',
    // Flavor (`create` vs `update`) lives on the prepared action — the
    // signal type itself is shared. Pull whichever flavor's payload is
    // present to render the right subtitle.
    formatLabel: (a) => {
      const action = a.prepared_actions?.find(
        (p) => p.type === 'create_contact' || p.type === 'update_contact'
      )
      return action?.type === 'update_contact'
        ? 'Update contact'
        : 'New contact'
    },
  },
  create_deal: {
    icon: PiTrendUp,
    linkTarget: 'company',
    formatLabel: () => 'New deal',
  },
  campaign_reply: {
    icon: PiMegaphone,
    linkTarget: 'company',
    formatLabel: (a) => {
      const pa = a.prepared_actions?.find(
        (p) => p.type === 'send_campaign_reply'
      )
      const interested =
        pa?.type === 'send_campaign_reply' && pa.is_interested === true
      return interested
        ? 'Interested reply'
        : `${a.days_since_reply ?? 0}d since reply`
    },
  },
}

// Per-action execution lives on the server — see POST
// /recommendations/<rec_id>/apply/ and ApplyRecommendationUsecase.
// The client just packages user inputs into a single FormData and posts;
// auth, validation, side effects, atomicity are enforced server-side.
